import React, { useState, useEffect } from 'react';
import { Button, Layout, Menu, message } from 'antd';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import axios from 'axios'; // 用来请求获取用户信息接口
import LoginPage from './pages/LoginPage';
import CreateWallet from './pages/wallet/CreateWallet';
import ManageWallet from './pages/wallet/ManageWallet';
import BatchTransferIn from './pages/wallet/BatchTransferIn';
import BatchTransferOut from './pages/wallet/BatchTransferOut';
import CreateProject from './pages/project/CreateProject';
import AddLiquidityAndBuy from './pages/project/AddLiquidityAndBuy';
import ProjectGroupManagement from './pages/project/ProjectGroupManagement';

const { Sider, Content, Header } = Layout;

const App = () => {
    const [isLoggedIn, setIsLoggedIn] = useState(() => localStorage.getItem('isLoggedIn') === 'true');
    const [userInfo, setUserInfo] = useState(null); // 用来存储用户信息

    const handleLogin = () => {
        setIsLoggedIn(true);
        localStorage.setItem('isLoggedIn', 'true');
    };

    const handleLogout = () => {
        setIsLoggedIn(false);
        localStorage.removeItem('isLoggedIn');
        localStorage.removeItem('token'); // 清除 token
        setUserInfo(null); // 清除用户信息
    };

    useEffect(() => {
        if (isLoggedIn) {
            const fetchUserInfo = async () => {
                try {
                    const token = localStorage.getItem('token');
                    const response = await axios.get('/api/v1/auth', {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    });

                    if (response.data.code === 0) {
                        const { username, last_login } = response.data.data;
                        localStorage.setItem('username', username);
                        localStorage.setItem('last_login', last_login);
                        setUserInfo({ username, last_login });
                    } else {
                        message.error('获取用户信息失败');
                    }
                } catch (error) {
                    message.error('网络错误，获取用户信息失败');
                }
            };
            fetchUserInfo();
        }
    }, [isLoggedIn]);

    if (!isLoggedIn) {
        return <LoginPage onLogin={handleLogin} />;
    }

    return (
        <Router>
            <MainLayout handleLogout={handleLogout} userInfo={userInfo} />
        </Router>
    );
};

// 主布局组件
const MainLayout = ({ handleLogout, userInfo }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');

        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };

    // 动态设置选中的菜单项
    const getSelectedKeys = () => {
        const pathParts = location.pathname.split('/');
        if (pathParts.length > 2) {
            return [pathParts.slice(1).join('/')]; // 提取 `wallet/create-wallet` 等路径
        }
        return [];
    };

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Sider width={250} style={{ background: '#001529' }}>
                <Menu
                    theme="dark"
                    mode="inline"
                    selectedKeys={getSelectedKeys()}
                    defaultOpenKeys={['wallet']} // 默认展开钱包菜单
                    onClick={({ key }) => navigate(`/${key}`)} // 直接跳转到 key 对应路径
                >
                    <Menu.SubMenu key="wallet" title="钱包">
                        <Menu.Item key="wallet/create-wallet">创建钱包</Menu.Item>
                        <Menu.Item key="wallet/manage-wallet">管理钱包</Menu.Item>
                        <Menu.Item key="wallet/batch-transfer-in">批量转入</Menu.Item>
                        <Menu.Item key="wallet/batch-transfer-out">批量转出</Menu.Item>
                    </Menu.SubMenu>
                    <Menu.SubMenu key="project" title="项目">
                        <Menu.Item key="project/create-project">创建项目</Menu.Item>
                        <Menu.Item key="project/add-liquidity-and-buy">添加流动性&购买</Menu.Item>
                        <Menu.Item key="project/project-group-management">项目组管理&交易</Menu.Item>
                    </Menu.SubMenu>
                </Menu>
            </Sider>
            <Layout>
                <Header style={{ background: '#fff', padding: 0 }}>
                    {userInfo && (
                        <div
                            style={{
                                float: 'left',
                                margin: '16px',
                                fontSize: '16px', // 调整字体大小
                                fontWeight: 'bold', // 加粗文字
                                verticalAlign: 'middle', // 使文字和按钮在同一水平线上
                                lineHeight: '32px', // 调整行高，使文字垂直居中
                            }}
                        >
                            你好, {userInfo.username}! 上次登录时间：{formatDate(userInfo.last_login)}
                        </div>
                    )}
                    <Button
                        type="primary"
                        danger
                        onClick={handleLogout}
                        style={{
                            float: 'right',
                            margin: '16px',
                            fontSize: '16px',
                            fontWeight: 'bold',
                        }}
                    >
                        退出登录
                    </Button>
                </Header>

                <Content style={{ margin: '24px 16px', padding: 24, background: '#fff' }}>
                    <Routes>
                        <Route path="login" element={<LoginPage />} />

                        {/* 钱包页面 */}
                        <Route path="wallet/create-wallet" element={<CreateWallet />} />
                        <Route path="wallet/manage-wallet" element={<ManageWallet />} />
                        <Route path="wallet/batch-transfer-in" element={<BatchTransferIn />} />
                        <Route path="wallet/batch-transfer-out" element={<BatchTransferOut />} />

                        {/* 项目页面 */}
                        <Route path="project/create-project" element={<CreateProject />} />
                        <Route path="project/add-liquidity-and-buy" element={<AddLiquidityAndBuy />} />
                        <Route path="project/project-group-management" element={<ProjectGroupManagement />} />

                        {/* 默认跳转，仅针对根路径 */}
                        <Route path="/" element={<Navigate to="/wallet/create-wallet" />} />

                        {/* 未匹配路径显示 404 页面 */}
                        <Route path="*" element={<div>404 页面未找到</div>} />
                    </Routes>
                </Content>
            </Layout>
        </Layout>
    );
};

export default App;
